import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, cachedGet, download, errMsg } from "@/lib/api";
import { Btn, Loading, Pick, fmtDate, fmtDateTime } from "@/components/app/bits";
import { useSystems } from "./ChecksheetsTab";

export default function P6Tab({ project }) {
  const { can } = useAuth();
  const systems = useSystems(project.id);
  const [data, setData] = useState(null);
  const [flagged, setFlagged] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => cachedGet(`/projects/${project.id}/p6`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [project.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { const r = await api.post(`/projects/${project.id}/p6/upload`, fd); toast.success(`${r.data.activities} activities imported, ${r.data.auto_mapped} mapped automatically`); load(); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  const map = async (a, value) => {
    const [kind, id] = value ? value.split(":") : [null, null];
    try { await api.put(`/p6/${a.id}`, kind === "sub" ? { subsystem_id: id } : { system_id: id }); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const options = systems.flatMap((s) => [[`sys:${s.id}`, `${s.code} ${s.name}`], ...s.subsystems.map((b) => [`sub:${b.id}`, `   ${b.code} ${b.name}`])]);

  if (!data) return <Loading />;
  const rows = data.activities.filter((a) => !flagged || a.flags.length);
  const nFlag = data.activities.filter((a) => a.flags.length).length;
  return (
    <div className="space-y-4" data-testid="p6-tab">
      <div className="panel p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-slate-100">Primavera P6 programme link</p>
          <p className="text-sm text-slate-400">{data.last_import ? `Last import: ${data.last_import.filename}, ${data.last_import.activities} activities, ${fmtDateTime(data.last_import.at)}` : "No programme imported yet."}</p>
        </div>
        <div className="flex gap-2">
          <Btn variant="ghost" onClick={() => download(`/projects/${project.id}/p6/sample.xer`, `${project.code}_sample.xer`).catch((e) => toast.error(errMsg(e)))} data-testid="p6-sample-btn"><Download className="w-4 h-4" />Sample XER</Btn>
          {can("Manager") && (
            <label className={`btn-accent px-4 py-2 text-sm flex items-center gap-2 cursor-pointer ${busy ? "opacity-50" : ""}`} data-testid="p6-upload-btn">
              <Upload className="w-4 h-4" />{busy ? "Importing…" : "Upload XER"}<input type="file" accept=".xer,.txt" className="hidden" onChange={upload} disabled={busy} data-testid="p6-upload-input" />
            </label>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400" data-testid="p6-flag-count"><AlertTriangle className="w-4 h-4 inline text-amber-500 mr-1" />{nFlag} activities where completions progress disagrees with the programme</p>
        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={flagged} onCheckedChange={setFlagged} data-testid="p6-flagged-toggle" />Flagged only</label>
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]" data-testid="p6-table">
          <thead><tr className="border-b border-[#1E2D4A] text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
            {["Activity", "Finish", "P6 %", "Field %", "Mapped to", "Flags"].map((h) => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="border-b border-[#1E2D4A] last:border-0 align-top" data-testid={`p6-row-${a.task_code}`}>
                <td className="px-3 py-3"><p className="num text-xs text-slate-500">{a.task_code}</p><p className="text-slate-200">{a.name}</p><p className="text-[10px] text-slate-500 font-mono">STAGES {a.stages.join("+")}</p></td>
                <td className="px-3 py-3 num text-slate-300 whitespace-nowrap">{fmtDate(a.finish)}</td>
                <td className="px-3 py-3 num text-slate-200">{a.p6_pct}%</td>
                <td className="px-3 py-3 num text-amber-400">{a.field_pct === null ? "-" : `${a.field_pct}%`}</td>
                <td className="px-3 py-3 w-64">
                  {can("Manager") ? <Pick testid={`p6-map-${a.task_code}`} value={a.subsystem_id ? `sub:${a.subsystem_id}` : a.system_id ? `sys:${a.system_id}` : ""} onChange={(v) => map(a, v)} placeholder="Not mapped" allLabel="Not mapped" options={options} />
                    : <span className="text-slate-400">{systems.find((s) => s.id === a.system_id)?.code || "Not mapped"}</span>}
                </td>
                <td className="px-3 py-3">{a.flags.map((f) => <p key={f} className="text-xs text-red-400 flex gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{f}</p>)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="py-10 text-center text-slate-500">No activities to show.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
